/** Playground screen (tenant/playground.html, P6 group `build`). */
export const playground = {
  title: "Playground",
  config: {
    title: "Configuration",
    sheetTitle: "Playground settings",
    open: "Configuration",
    unsaved: "Unsaved",
    unsavedHint: "Save to agent to test these changes — test answers use the agent's saved settings.",
    model: "Model",
    modelOption: "{{name}} · {{descriptor}}",
    creditsHint_one: "Uses {{count}} message credit per answer.",
    creditsHint_other: "Uses {{count}} message credits per answer.",
    temperature: "Temperature",
    precise: "Precise",
    creative: "Creative",
    strict: "Answer only from knowledge",
    strictHint: "If no source is found, say so and offer handoff — never guess.",
    citation: "Citation style",
    citations: { numbered: "Inline [1]", source_names: "Sources below", hidden: "Off" },
    audience: "Simulate audience",
    audienceHint: "Access filtering changes retrieval: chunks the audience can't see are removed before ranking.",
    audienceEffective: "This agent lets that audience read: {{labels}}",
    audienceClosed: "This agent is closed to that audience (limited by “Who can chat”), so it refuses their messages. Pick another audience to test it.",
    language: "Reply language",
    languages: { match: "Match the user", english: "Always English" },
    instructions: "System instructions",
    tokens: "≈ {{count}} tokens",
    reset: "Reset",
    resetDone: "Configuration reset to the agent's saved settings",
    save: "Save to agent",
    saved: "Saved to {{name}} — live in all channels within a minute",
  },
  chat: {
    subtitle: "{{model}} · {{audience}} · {{count}} sources",
    defaultModel: "Default model",
    badge: "Playground — not visible to users",
    showTrace: "Show trace",
    clear: "Clear chat",
    cleared: "New test conversation started",
    audienceClosedError: "This agent is closed to “{{audience}}” (limited by “Who can chat”). Pick another audience under Simulate audience to test it.",
    starters: "Suggested questions",
    welcomeBody: "Ask anything the people you serve would ask. The trace panel shows exactly which documents were searched and why. Nothing here is saved to the Inbox.",
    placeholder: "Ask a test question… (Enter to send, Shift+Enter for new line)",
    footer: "Playground messages use test credits and are excluded from Analytics.",
    log: "Test conversation",
    stop: "Stop",
    retry: "Try again",
    planTitle: "Playground credits",
  },
  stages: {
    understanding: "Reading the question…",
    retrieving: "Searching your knowledge…",
    reranking: "Checking the best sources…",
    generating: "Writing the answer…",
    validating: "Double-checking the answer…",
  },
  trace: {
    title: "Retrieval trace",
    sheetTitle: "Debug",
    tabs: { trace: "Trace", sources: "Sources used", json: "Raw JSON" },
    status: { idle: "Idle", running: "Running", grounded: "Grounded", fallback: "Fallback", error: "Unavailable" },
    emptyTitle: "No trace yet",
    emptyBody: "Send a question to see the rewritten query, which chunks were retrieved and why, latency per stage, and token cost.",
    noSources: "No sources yet",
    nothing: "Nothing to show",
    loadError: "The trace couldn't load.",
    running: "Waiting for the answer to finish…",
    query: "Query",
    asked: "User asked",
    rewritten: "Rewritten for search",
    notRewritten: "Used as asked",
    language: "Language: {{language}}",
    latency: "Latency",
    total: "{{value}} total",
    legendRetrieval: "Retrieval",
    legendFirst: "Time to first token",
    legendGeneration: "Generation",
    access: "Access filter",
    accessBody: "Searched only sources visible to: {{audiences}}. Chunks other audiences can see were removed before ranking.",
    cache: "Cache: {{kind}}",
    cacheMiss: "No cache hit",
    chunks: "Retrieved candidates",
    chunkCounts: "{{used}} used · {{filtered}} filtered",
    used: "Used in answer",
    filtered: "Not used",
    ranks: { dense: "vector", lexical: "keyword", question: "question", fuzzy: "fuzzy", rrf: "RRF", rerank: "rerank" },
    rank: "#{{value}}",
    untitled: "Untitled source",
    openSource: "Open source",
    noChunks: "No candidates were retrieved for this question.",
    tokens: "Tokens & cost",
    input: "Input",
    output: "Output",
    cost: "Est. cost",
    model: "Model",
    embedding: "Embedding",
    reranker: "Reranker",
    search: "Search",
    grounding: "Grounding",
    grounded: "Grounded",
    notGrounded: "Not grounded",
    confidence: "confidence {{value}}",
    groundedBody: "Every claim in the answer maps to a cited chunk.",
    ungroundedBody: "No chunk scored above the threshold, so the agent declined and offered a handoff. The question was logged in Knowledge Gaps.",
    gapsLink: "Knowledge Gaps",
    noSourcesTitle: "No sources used",
    noSourcesBody: "The agent found nothing relevant and declined to answer. Add a Q&A or document to fill this gap.",
    answerGap: "Answer this gap",
    copy: "Copy",
    copied: "Copied",
    endpoint: "GET /v1/messages/{{id}}/trace",
  },
} as const;
